import type { ModalProps } from '@sparklayer-io/spark-dash-ui/components/ui/modal/types';
import { LearnMoreLink, Modal, Text } from '@ui/themed';
import { t } from 'i18next';
import type { PropsWithChildren, ReactNode } from 'react';
import type { I18nPrefix } from '../../../i18n/prefix';

export const COMMON = 'features:partners.components.common' satisfies I18nPrefix;

export type PartnerModalAction = NonNullable<ModalProps['primaryAction']>;

/**
 * The frame every partner integration modal shares. Each partner supplies its own
 * `primaryAction` because the confirm differs — see `saveAction` and `authoriseAction`.
 */
export function PartnerModal({
  isOpen,
  onClose,
  title,
  banner,
  description,
  docsUrl,
  primaryAction,
  size,
  children,
}: PropsWithChildren<{
  isOpen: boolean;
  onClose: () => void;
  title: string;
  /** Alert above everything else, for a prerequisite the merchant hasn't met yet. */
  banner?: ReactNode;
  /** Intro copy above the fields. Omit to supply your own via `children`. */
  description?: ReactNode;
  docsUrl?: string;
  primaryAction: PartnerModalAction;
  size?: ModalProps['size'];
}>) {
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size={size}
      secondaryAction={{ text: t(`${COMMON}.close`), onClick: onClose }}
      primaryAction={primaryAction}
    >
      <Modal.Header>{title}</Modal.Header>
      <Modal.Body>
        <div className="flex flex-col gap-4">
          {banner}
          {description != null && (
            <Text variant="secondary">
              {description} {docsUrl && <LearnMoreLink link={docsUrl} />}
            </Text>
          )}
          {children}
        </div>
      </Modal.Body>
    </Modal>
  );
}

/** The usual confirm: saves the settings. */
export function saveAction(onClick: () => void, isSaving: boolean): PartnerModalAction {
  return { text: t(`${COMMON}.save`), onClick, loading: isSaving };
}

/** The confirm shown until a partner's OAuth handshake completes. */
export function authoriseAction(
  partner: string,
  onClick: () => void,
  options?: { disabled?: boolean; loading?: boolean },
): PartnerModalAction {
  return {
    text: t(`${COMMON}.authorise_access`, { partner }),
    onClick,
    disabled: options?.disabled,
    loading: options?.loading,
  };
}
